import type { LibraryFilterDraft } from '@/features/publicsite/types/registry/LibraryFilterDraft';
import { libraryApi } from '@/services/libraryApi';

/** The filters the backend answers, beyond free text. */
export const FILTER_KEYS = ['author', 'institution', 'year', 'type', 'domain', 'keyword'] as const;

/** Everything the public library endpoint accepts, including the free text and the domain. */
const FORWARDED = ['q', ...FILTER_KEYS] as const;

/**
 * Which of the page's two groups to show — this library's own holdings, or the international
 * catalogues it links out to.
 *
 * Deliberately NOT forwarded to the backend. The international entries are link-outs to FAO and
 * CORDIS, not rows in our database: there is nothing for `/api/v1/public/library` to filter by
 * source, and sending it would be asking the server a question it cannot answer. It is read by
 * the page instead, which is what decides whether each group renders.
 */
export const SOURCE_KEY = 'source';

/** Every parameter that must survive paging, filters plus the source the backend never sees. */
const PRESERVED = [...FORWARDED, SOURCE_KEY] as const;

/** The form's fields for a given URL — the filters as the reader last left them. */
export function draftFrom(params: URLSearchParams): LibraryFilterDraft {
  return {
    source: params.get(SOURCE_KEY) ?? '',
    q: params.get('q') ?? '',
    author: params.get('author') ?? '',
    institution: params.get('institution') ?? '',
    year: params.get('year') ?? '',
    type: params.get('type') ?? '',
    domain: params.get('domain') ?? '',
    keyword: params.get('keyword') ?? '',
  };
}

/**
 * The library request for a given set of URL parameters.
 *
 * <p>Derived from one list rather than from named variables: the page used to read `q` by hand and
 * drop everything else, so a filter added to the form would have been sent nowhere. Anything not
 * on the list is ignored rather than forwarded — the URL is user-editable, and this endpoint is
 * public.
 */
export function libraryQuery(params: URLSearchParams, page = 0, size = 12): string {
  const search = new URLSearchParams();
  FORWARDED.forEach((key) => {
    const value = params.get(key)?.trim();
    if (value) search.set(key, value);
  });
  search.set('page', String(page));
  search.set('size', String(size));
  return libraryApi.searchUrl(search);
}

/**
 * The URL of another page of the current result set.
 *
 * <p>Carries every active filter across, so paging a filtered list does not silently drop the
 * filters — and page zero is written as a bare path so the first page has one address rather
 * than two.
 */
export function libraryPageHref(params: URLSearchParams, page: number): string {
  const next = new URLSearchParams();
  PRESERVED.forEach((key) => {
    const value = params.get(key)?.trim();
    if (value) next.set(key, value);
  });
  if (page > 0) next.set('page', String(page));
  return next.toString() ? `/biblioteca?${next}` : '/biblioteca';
}
